import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sparkles } from "lucide-react";
import FeedbackWidget from "../components/FeedbackWidget";
import WhatIfSandbox from "../components/WhatIfSandbox";
import { saveToHistory, getHistory } from "../lib/history";
import { api } from "../lib/api";
import { toast } from "sonner";
import ProductTour, {
  REPORT_TOUR_KEY,
  REPORT_TOUR_STEPS,
} from "../components/ProductTour";
import ProUnlockDialog from "../components/ProUnlockDialog";
import SiteFootnote from "../components/SiteFootnote";
import PlainEnglishConclusion from "../components/report/PlainEnglishConclusion";
import BenchmarkCard from "../components/report/BenchmarkCard";
import AiNarrativeCard from "../components/report/AiNarrativeCard";
import HealthScoreCard from "../components/report/HealthScoreCard";
import SubScoresCard from "../components/report/SubScoresCard";
import SnapshotCard from "../components/report/SnapshotCard";
import ValuationSnapshotCard from "../components/report/ValuationSnapshotCard";
import WhatsNewBanner from "../components/WhatsNewBanner";
import {
  ProfitabilityGrid,
  DebtSolvencyGrid,
  LiquidityGrid,
  EfficiencyGrid,
} from "../components/report/MetricGrids";
import InsightsSection from "../components/report/InsightsSection";
import Header from "../components/report/Header";
import PrintCover from "../components/report/PrintCover";
import { SampleModeBanner, SampleModeStickyCta } from "../components/report/SampleMode";
import { BetaBanner } from "../components/report/Banners";
import { ReportHeader, SectionTitle } from "../components/report/Headings";
import {
  hasProEntitlement,
  hasPdfEntitlement,
  verifyEntitlement,
  fetchReportByToken,
  getProToken,
  getExpiredEntitlement,
  buildPdfDownloadUrl,
} from "../lib/pro";
import ExpiredPassBanner from "../components/report/ExpiredPassBanner";
export default function ReportPage() {
  const [report, setReport] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [savedThis, setSavedThis] = useState(false);
  const [entitlement, setEntitlement] = useState(null);
  const [expiredEntitlement, setExpiredEntitlement] = useState(() => getExpiredEntitlement());
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [searchParams] = useSearchParams();
  // Sample mode is set by SampleReportPage when the visitor lands at
  // /sample-report. In sample mode we force-unlock the report (AI memo + PDF)
  // and show a sticky CTA so the visitor sees exactly what a paying customer
  // gets. No backend entitlement is created — the unlock is purely local
  // and survives only as long as the session.
  const [sampleMode] = useState(
    () =>
      typeof window !== "undefined" &&
      sessionStorage.getItem("ll_sample_mode") === "1"
  );
  const navigate = useNavigate();

  useEffect(() => {
    const stored = sessionStorage.getItem("ll_last_report");
    // Recovery path: if sessionStorage is empty (cleared between analysis
    // and payment — different device, incognito, etc.), try to recover the
    // report from the backend using the unlock_token. Either:
    //   1. `?report=<id>&token=<unlock>` URL params (one-shot recovery link)
    //   2. The stored entitlement (typical post-payment case)
    if (!stored) {
      const urlReportId = searchParams.get("report");
      const urlToken = searchParams.get("token");
      const storedToken = getProToken();
      // Prefer URL params over storage so a paid user can land on a fresh
      // device and have the report appear immediately.
      const recoveryReportId = urlReportId;
      const recoveryToken = urlToken || storedToken;
      if (recoveryReportId && recoveryToken) {
        fetchReportByToken(recoveryReportId, recoveryToken).then((res) => {
          if (res?.report) {
            sessionStorage.setItem(
              "ll_last_report",
              JSON.stringify(res.report)
            );
            setReport(res.report);
            // Trigger entitlement verify so the gated sections appear.
            verifyEntitlement().then((ent) => {
              setEntitlement(ent);
              setExpiredEntitlement(getExpiredEntitlement());
            });
            setHistoryCount(getHistory().length);
            toast.success("Welcome back — your report is restored.");
          } else {
            navigate("/analyze");
          }
        });
        return;
      }
      navigate("/analyze");
      return;
    }
    try {
      setReport(JSON.parse(stored));
    } catch {
      navigate("/analyze");
    }
    setHistoryCount(getHistory().length);
    verifyEntitlement().then((ent) => {
      setEntitlement(ent);
      setExpiredEntitlement(getExpiredEntitlement());
    });
  }, [navigate, searchParams]);

  // Auto-save the current report to local history whenever it changes.
  // The manual "Save to History" button used to be the only way this happened,
  // which meant most returning users never saw the period-over-period delta.
  // We now silently upsert on view — sample-mode runs are excluded inside
  // saveToHistory itself.
  useEffect(() => {
    if (!report) return;
    const next = saveToHistory(report);
    if (next) setHistoryCount(next.length);
  }, [report]);

  function handleSaveToHistory() {
    if (!report) return;
    const next = saveToHistory(report);
    setHistoryCount(next?.length ?? 0);
    setSavedThis(true);
    toast.success("Saved. View Trends to compare periods.");
  }

  if (!report) return null;

  const m = report.metrics || {};
  const agg = m._aggregates || {};
  const h = report.health || {};
  const findings = report.findings || {};
  const input = report.input || {};
  const currency = input.currency || "USD";
  const proActive = sampleMode || hasProEntitlement(entitlement);
  const pdfActive = sampleMode || hasPdfEntitlement(entitlement, report.id);

  // For the returning-user enterprise-value delta on the Valuation snapshot,
  // find the most recent OTHER period in local history that had a valuation.
  const priorValuation = (() => {
    try {
      const hist = getHistory();
      const prior = hist.find((entry) => entry.id !== report.id && entry.valuation);
      return prior?.valuation || null;
    } catch {
      return null;
    }
  })();

  function handlePrintClick() {
    if (pdfActive || proActive) {
      // Surface the "Save as PDF" hint visibly BEFORE the print dialog steals
      // focus. The previous toast-after-delay pattern was effectively hidden
      // by the OS print dialog. A 1.6s visible toast + window.print gives
      // the user a chance to read the tip before the modal opens.
      toast.message("Tip: choose 'Save as PDF' as the destination", {
        description: "The cover page, score, and all sections are pre-formatted for A4.",
        duration: 4000,
      });
      setTimeout(() => window.print(), 1200);
    } else {
      setUnlockOpen(true);
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative" data-testid="report-page">
      <WhatsNewBanner />
      <ProductTour steps={REPORT_TOUR_STEPS} storageKey={REPORT_TOUR_KEY} />
      <Header
        report={report}
        onSave={handleSaveToHistory}
        savedThis={savedThis}
        historyCount={historyCount}
        onPrint={handlePrintClick}
        pdfActive={pdfActive}
        proActive={proActive}
      />
      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-12 relative">
        {sampleMode && <SampleModeBanner />}
        <PrintCover input={input} health={h} report={report} />
        {!proActive && expiredEntitlement && (
          <ExpiredPassBanner entitlement={expiredEntitlement} />
        )}
        <BetaBanner />
        <ReportHeader input={input} health={h} />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <HealthScoreCard health={h} />
          <SubScoresCard subscores={h.sub_scores || {}} />
          <SnapshotCard agg={agg} m={m} currency={currency} />
        </div>

        {/* Free-tier enterprise-value headline. Full lender/buyer breakdown +
            methodology stays behind Pro Pass in the Insights section. */}
        <ValuationSnapshotCard
          valuation={report.valuation_range}
          priorValuation={priorValuation}
          proActive={proActive}
          onUnlock={() => setUnlockOpen(true)}
        />

        <SectionTitle
          eyebrow="Profitability"
          title="Where the money lives."
          learnSlug="ebitda-margin"
          learnLabel="Read about profitability in 30s"
        />
        <ProfitabilityGrid m={m} currency={currency} />

        <SectionTitle
          eyebrow="Debt & Solvency"
          title="Can the business carry its load?"
          learnSlug="dscr"
          learnLabel="Read about DSCR in 30s"
        />
        <DebtSolvencyGrid m={m} agg={agg} currency={currency} />

        <SectionTitle
          eyebrow="Liquidity"
          title="Can you cover next month?"
          learnSlug="quick-ratio"
          learnLabel="Read about quick ratio in 30s"
        />
        <LiquidityGrid m={m} currency={currency} />

        <SectionTitle
          eyebrow="Efficiency & Cash Flow"
          title="How fast does the business cycle cash?"
          learnSlug="cash-conversion-cycle"
          learnLabel="Read about cash cycle in 30s"
        />
        <EfficiencyGrid m={m} currency={currency} />

        <InsightsSection
          report={report}
          health={h}
          findings={findings}
          input={input}
          proActive={proActive}
          sampleMode={sampleMode}
          onUnlock={() => setUnlockOpen(true)}
        />

        {report.benchmark && (
          <>
            <SectionTitle eyebrow="Peer Benchmarks" title="How you stack up against peers." />
            <BenchmarkCard
              global={report.benchmark}
              region={report.benchmark_region}
              regionCode={report.region}
            />
          </>
        )}

        <SectionTitle
          eyebrow="Advisor Memo"
          title={report.audience === "analyst" ? "An underwriter's read on this borrower." : "A senior CFO's read on your numbers."}
          proOnly
          sampleMode={sampleMode}
        />
        <AiNarrativeCard
          text={report.ai_narrative}
          audience={report.audience}
          proActive={proActive}
          onUnlock={() => setUnlockOpen(true)}
        />

        {report.plain_english_summary && (
          <>
            <SectionTitle
              eyebrow="In Plain English"
              title="What it all means, without the jargon."
              proOnly
              sampleMode={sampleMode}
            />
            <PlainEnglishConclusion
              summary={report.plain_english_summary}
              proActive={proActive}
              onUnlock={() => setUnlockOpen(true)}
              report={report}
            />
          </>
        )}

        <p className="text-xs text-[#5C665D] mt-12 leading-relaxed border-t border-[#E5E0D8] pt-6 max-w-4xl">
          Generated automatically from the figures provided. Industry benchmarks are typical
          mid-range estimates for SMB operators in the selected sector.
        </p>
        <div className="print-only text-[10px] uppercase tracking-[0.18em] text-[#5C665D] mt-12 pt-6 border-t border-[#E5E0D8] flex justify-between">
          <span>CFO Grade · Educational insights for business owners — not financial advice.</span>
          <span>{(report.id || "").slice(0, 8)}</span>
        </div>
      </main>
      <FeedbackWidget
        context={{
          audience: report.audience,
          industry: input.industry,
          health_score: h.overall_score,
          rating_label: h.rating,
        }}
      />
      <ProUnlockDialog
        open={unlockOpen}
        onOpenChange={setUnlockOpen}
        reportId={report.id}
      />
      {sampleMode && <SampleModeStickyCta />}
    </div>
  );
}